import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { DataGrid, GridColDef, GridRowModel } from "@mui/x-data-grid";
import { Box, Checkbox, Typography, Paper } from "@mui/material";
import dayjs from "dayjs";
import useAuth from "../../../hooks/useAuth";
import { apiVariable } from "../../../api/apiVariable";

const fetchReminders = async (token: string | null | undefined) => {
  const { data } = await axios.get(`${apiVariable}/reminder`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return data;
};

const updateReminder = async ({
  token,
  reminder,
}: {
  token: string | null | undefined;
  reminder: any;
}) => {
  const { data } = await axios.put(`${apiVariable}/reminder`, reminder, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  return data;
};

const RemindersTable = () => {
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const {
    data: reminders,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["reminders"],
    queryFn: () => fetchReminders(token),
  });

  const mutation = useMutation({
    mutationFn: updateReminder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reminders"] });
    },
  });

  const handleCompletionToggle = (reminder: any) => {
    mutation.mutate({
      token,
      reminder: { ...reminder, isCompleted: !reminder.isCompleted },
    });
  };

  const processRowUpdate = async (
    newRow: GridRowModel,
    oldRow: GridRowModel,
  ) => {
    try {
      const updatedReminder = { ...oldRow, ...newRow };
      await mutation.mutateAsync({ token, reminder: updatedReminder });
      return updatedReminder;
    } catch (error) {
      console.error("Error updating reminder:", error);
      return oldRow;
    }
  };

  const columns: GridColDef[] = [
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      editable: true,
      renderCell: (params) => (
        <Typography
          sx={{
            textDecoration: params.row.isCompleted ? "line-through" : "none",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "scheduledTime",
      headerName: "Scheduled Time",
      flex: 1,
      renderCell: (params) => (
        <Typography
          sx={{
            color:
              dayjs(params.value).isBefore(dayjs()) && !params.row.isCompleted
                ? "red"
                : "inherit",
            textDecoration: params.row.isCompleted ? "line-through" : "none",
          }}
        >
          {dayjs(params.value).format("MM/DD/YYYY")}
        </Typography>
      ),
    },
    {
      field: "leadCompany",
      headerName: "Company",
      flex: 1,
      renderCell: (params) => (
        <Typography
          sx={{
            textDecoration: params.row.isCompleted ? "line-through" : "none",
          }}
        >
          {params.row.lead?.company ?? ""}
        </Typography>
      ),
    },
    {
      field: "leadDOT",
      headerName: "DOT",
      flex: 1,
      renderCell: (params) => (
        <Typography
          sx={{
            textDecoration: params.row.isCompleted ? "line-through" : "none",
          }}
        >
          {params.row.lead?.dot ?? ""}
        </Typography>
      ),
    },
    {
      field: "isCompleted",
      headerName: "Completed",
      flex: 0.5,
      renderCell: (params) => (
        <Checkbox
          checked={params.value}
          onChange={() => handleCompletionToggle(params.row)}
        />
      ),
    },
  ];

  if (isLoading) {
    return <Typography>Loading reminders...</Typography>;
  }

  if (isError) {
    return <Typography color="error">Error: {error.message}</Typography>;
  }

  return (
    <Paper
      elevation={3}
      sx={{
        padding: 2,
        margin: { xs: 0, sm: 2 },
        maxWidth: { xs: "100%", sm: "calc(100% - 32px)" },
        width: { xs: "100%", md: "auto" },
        borderRadius: 2,
        backgroundColor: "#f9f9f9",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          marginBottom: 2,
          fontWeight: "bold",
          color: "#3f51b5",
        }}
      >
        Reminders
      </Typography>
      <DataGrid
        rows={reminders || []}
        columns={columns}
        // autoHeight

        getRowId={(row) => row.id}
        processRowUpdate={processRowUpdate}
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#3f51b5",
            color: "#fff",
            fontSize: "1rem",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #e0e0e0",
          },
          "& .MuiCheckbox-root": {
            color: "#3f51b5",
          },
          height: 400,
          width: "900px",
          maxWidth: "100%",
          overflowY: "auto",
        }}
      />
    </Paper>
  );
};

export default RemindersTable;
